<template>
  <!-- Edit Cruise Dialog -->
  <TransitionRoot appear :show="dialogs.editCruise" as="template">
    <Dialog @close="closeDialog('editCruise')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <EditCruiseForm
                v-if="selectedItems.cruiseForEdit"
                :cruise="selectedItems.cruiseForEdit"
                @updated="$emit('cruise-updated')"
                @close="closeDialog('editCruise')"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Add Duck Dialog -->
  <TransitionRoot appear :show="dialogs.addDuck" as="template">
    <Dialog @close="closeDialog('addDuck')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <AddDuckForm
                v-if="selectedItems.cruiseId"
                :cruise-id="selectedItems.cruiseId"
                :cruise-info="getCruiseInfo(selectedItems.cruiseId)"
                :warning-message="warningMessage"
                @created="$emit('duck-created')"
                @close="closeDialog('addDuck')"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Duck Details Dialog -->
  <TransitionRoot appear :show="dialogs.duckDetails" as="template">
    <Dialog @close="closeDialog('duckDetails')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DuckDetailsDialog
                v-if="selectedItems.duck"
                :duck="selectedItems.duck"
                @close="closeDialog('duckDetails')"
                @updated="$emit('duck-updated')"
                @refresh-cruises="$emit('refresh-cruises')"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Reset Confirmation Dialog -->
  <TransitionRoot appear :show="dialogs.resetConfirm" as="template">
    <Dialog @close="closeDialog('resetConfirm')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 mb-4">
                Reset All Ducks
              </DialogTitle>
              <div class="mb-4">
                <p class="text-sm text-gray-500 mb-2">Are you sure you want to reset all ducks for this cruise?</p>
                <p class="text-xs text-red-600">This action cannot be undone.</p>
              </div>

              <div class="flex justify-end space-x-3">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  @click="closeDialog('resetConfirm')"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                  @click="$emit('reset-confirmed')"
                  :disabled="loadingStates.resetting"
                >
                  <svg
                    v-if="loadingStates.resetting"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Reset Ducks
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Cruise Messages Dialog -->
  <TransitionRoot appear :show="dialogs.cruiseMessages" as="template">
    <Dialog @close="closeDialog('cruiseMessages')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-0 sm:p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full sm:max-w-2xl transform overflow-hidden sm:rounded-2xl bg-white text-left align-middle shadow-xl transition-all sm:my-8 mobile-dialog-fullscreen">
              <div class="h-[85vh] sm:h-[70vh] max-h-[90vh] sm:max-h-[70vh] flex flex-col">
                <MessageList
                  v-if="selectedItems.cruiseId && cruiseName"
                  :cruise-id="selectedItems.cruiseId"
                  :title="`Messages for ${cruiseName}`"
                  :hide-header="false"
                  @update="$emit('messages-updated', $event)"
                  @close="closeDialog('cruiseMessages')"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Duck Messages Dialog -->
  <TransitionRoot appear :show="dialogs.duckMessages" as="template">
    <Dialog @close="closeDialog('duckMessages')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-0 sm:p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full sm:max-w-2xl transform overflow-hidden sm:rounded-2xl bg-white text-left align-middle shadow-xl transition-all sm:my-8 mobile-dialog-fullscreen">
              <div class="h-[85vh] sm:h-[70vh] max-h-[90vh] sm:max-h-[70vh] flex flex-col">
                <MessageList
                  v-if="selectedItems.duckUuid && duckName"
                  :duck-uuid="selectedItems.duckUuid"
                  :title="`Messages for ${duckName}`"
                  :hide-header="false"
                  @update="$emit('messages-updated', $event)"
                  @close="closeDialog('duckMessages')"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>  
  <!-- Package Selection Dialog -->
  <TransitionRoot appear :show="dialogs.selectPackage" as="template">
    <Dialog @close="closeDialog('selectPackage')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 mb-4">
                Choose a Package
              </DialogTitle>
              
              <div class="mb-6">
                <p class="text-gray-600">
                  Select a package to enhance your duck expedition
                </p>
              </div>
              
              <div v-if="loadingPackages" class="flex justify-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              
              <div v-else-if="packages.length === 0" class="text-center py-6">
                <p class="text-gray-500">No packages available at the moment</p>
              </div>
              
              <div v-else class="grid gap-4 md:grid-cols-3 mb-6">
                <div
                  v-for="pkg in packages"
                  :key="pkg.id"
                  :class="[
                    'border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md',
                    selectedPackageId === pkg.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
                  ]"
                  @click="selectedPackageId = pkg.id"
                >
                  <h4 class="font-semibold text-lg mb-2">{{ pkg.name }}</h4>
                  <p class="text-gray-600 mb-3">{{ pkg.description }}</p>
                  <p class="font-bold text-xl mb-4">
                    {{ formatCurrency(pkg.pricePerDuck || pkg.price, pkg.currency) }} {{ pkg.pricePerDuck ? 'per duck' : '' }}
                  </p>
                  <ul class="text-sm text-gray-600 mb-4">
                    <li>• Up to {{ pkg.maxDucks }} ducks</li>
                  </ul>
                  <button
                    class="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors"
                    :disabled="selectedPackageId !== pkg.id"
                    @click="proceedToCheckout(pkg.id)"
                  >
                    Select
                  </button>
                </div>
              </div>
              
              <div v-if="selectedItems.cruiseId" class="border-t pt-6">
                <p class="mb-4 font-medium">This will upgrade: {{ cruiseName }}</p>
                <div class="max-w-xs">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Number of Ducks</label>
                  <input
                    v-model.number="quantity"
                    type="number"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter quantity"
                  />
                </div>
              </div>
              
              <div v-else class="border-t pt-6">
                <p class="mb-4 font-medium">Create a new cruise with this package</p>
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Cruise Name</label>
                    <input
                      v-model="newCruiseName"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter cruise name"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Number of Ducks</label>
                    <input
                      v-model.number="quantity"
                      type="number"
                      min="1"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter quantity"
                    />
                  </div>
                  
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        v-model="startDate"
                        type="date"
                        :min="minDate"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        v-model="endDate"
                        type="date"
                        :min="startDate || minDate"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                  @click="closeDialog('selectPackage')"
                  :disabled="checkoutProcessing"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  @click="confirmPackageSelection"
                  :disabled="!canProceedToCheckout || checkoutProcessing"
                >
                  <svg
                    v-if="checkoutProcessing"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Proceed to Checkout
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <!-- Buy More Ducks Dialog -->
  <TransitionRoot appear :show="dialogs.buyMoreDucks" as="template">
    <Dialog @close="closeDialog('buyMoreDucks')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 mb-4">
                Buy More Ducks
              </DialogTitle>
              
              <div class="mb-6">
                <p class="text-gray-600 mb-6">
                  Purchase additional ducks for your {{ cruiseName }} cruise
                </p>
                
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div class="flex justify-between mb-2">
                    <span class="text-sm text-gray-600">Current allowed ducks:</span>
                    <span class="font-medium">{{ selectedItems.cruiseData?.maxDucksAllowed || 0 }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm text-gray-600">New allowed ducks:</span>
                    <span class="font-medium">{{ (selectedItems.cruiseData?.maxDucksAllowed || 0) + additionalDucksQuantity }}</span>
                  </div>
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Number of Additional Ducks</label>
                  <input
                    v-model.number="additionalDucksQuantity"
                    type="number"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter quantity"
                  />
                </div>
                
                <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div class="flex justify-between mb-2">
                    <span class="text-sm text-gray-600">Price per duck:</span>
                    <span class="font-medium">{{ formatCurrency(selectedItems.cruiseData?.package?.pricePerDuck || 0, selectedItems.cruiseData?.package?.currency || 'USD') }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="font-semibold text-gray-900">Total price:</span>
                    <span class="font-bold text-gray-900">{{ formatCurrency((selectedItems.cruiseData?.package?.pricePerDuck || 0) * additionalDucksQuantity, selectedItems.cruiseData?.package?.currency || 'USD') }}</span>
                  </div>
                </div>
              </div>

              <div class="flex justify-end space-x-3">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                  @click="closeDialog('buyMoreDucks')"
                  :disabled="buyingMoreDucks"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  @click="confirmBuyMoreDucks"
                  :disabled="!additionalDucksQuantity || additionalDucksQuantity < 1 || buyingMoreDucks"
                >
                  <svg
                    v-if="buyingMoreDucks"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Proceed to Checkout
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogOverlay,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import EditCruiseForm from '@/components/admin/EditCruiseForm.vue';
import AddDuckForm from '@/components/admin/AddDuckForm.vue';
import DuckDetailsDialog from '@/components/admin/DuckDetailsDialog.vue';
import MessageList from '@/components/admin/MessageList.vue';
import type { DetailedCruise, DetailedDuck } from '@/interfaces/cruise';

// Package interface
interface Package {
  id: number;
  name: string;
  description: string;
  maxDucks: number;
  pricePerDuck?: number; // Make this optional
  price: number;
  currency: string;
}

interface DialogsState {
  editCruise: boolean;
  addDuck: boolean;
  duckDetails: boolean;
  resetConfirm: boolean;
  cruiseMessages: boolean;
  duckMessages: boolean;
  selectPackage: boolean;
  buyMoreDucks: boolean; // Add this new dialog state
}

interface LoadingStates {
  resetting: boolean;
}

interface SelectedItems {
  cruiseId: number | null;
  cruiseForEdit: DetailedCruise | null;
  duck: DetailedDuck | null;
  duckUuid: string | null;
  cruiseData?: DetailedCruise | null; // Add this to hold the full cruise data
}

const props = defineProps({
  dialogs: {
    type: Object as () => DialogsState,
    required: true
  },
  loadingStates: {
    type: Object as () => LoadingStates,
    required: true
  },
  selectedItems: {
    type: Object as () => SelectedItems,
    required: true
  },
  cruiseName: {
    type: String,
    default: ''
  },
  duckName: {
    type: String,
    default: ''
  },
  packages: {
    type: Array as () => Package[],
    default: () => []
  },
  loadingPackages: {
    type: Boolean,
    default: false
  },
  checkoutProcessing: {
    type: Boolean,
    default: false
  },
  warningMessage: {
    type: String,
    default: ''
  }
});

const emit = defineEmits([
  'update:dialogs',
  'cruise-updated',
  'duck-created',
  'duck-updated',
  'reset-confirmed',
  'messages-updated',
  'package-selected',
  'buy-more-ducks',
  'refresh-cruises'
]);

// Package selection variables
const selectedPackageId = ref<number | null>(null);
const newCruiseName = ref('');
const quantity = ref(1);
const startDate = ref('');
const endDate = ref('');

// Add state for buying more ducks
const additionalDucksQuantity = ref(1);
const buyingMoreDucks = ref(false);

// Calculate minimum allowed date (today)
const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

// Watch for dialog opening to reset state
watch(() => props.dialogs.selectPackage, (isOpen) => {
  if (isOpen) {
    selectedPackageId.value = null;
    newCruiseName.value = '';
    quantity.value = 1;
    startDate.value = '';
    endDate.value = '';
  }
});

// Add a new watch for the buyMoreDucks dialog
const selectedCruiseId = computed(() => props.selectedItems.cruiseId);

const canProceedToCheckout = computed(() => {
  if (!selectedPackageId.value) return false;
  
  // If upgrading existing cruise, no need for name
  if (selectedCruiseId.value) return true;
  
  // For new cruise, require a name and dates
  return !!newCruiseName.value.trim() && !!startDate.value && !!endDate.value;
});

const closeDialog = (dialogKey: keyof DialogsState) => {
  const updatedDialogs = { ...props.dialogs };
  updatedDialogs[dialogKey] = false;
  
  // Emit the updated dialogs object to parent
  // This allows the parent to track which dialog was closed
  emit('update:dialogs', updatedDialogs);
};

// Package selection methods
const formatCurrency = (price: number, currency: string) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(price);
};

const proceedToCheckout = (packageId: number) => {
  selectedPackageId.value = packageId;
};

const confirmPackageSelection = () => {
  if (!selectedPackageId.value || quantity.value < 1) return;
  
  emit('package-selected', {
    packageId: selectedPackageId.value,
    cruiseName: selectedCruiseId.value ? props.cruiseName : newCruiseName.value.trim(),
    quantity: quantity.value,
    startDate: startDate.value,
    endDate: endDate.value
  });
};

// Add method to handle buying more ducks
const confirmBuyMoreDucks = () => {
  if (!props.selectedItems.cruiseId || additionalDucksQuantity.value < 1) return;
  
  emit('buy-more-ducks', {
    cruiseId: props.selectedItems.cruiseId,
    quantity: additionalDucksQuantity.value
  });
};

// Add a helper method to get cruise info for the AddDuckForm
const getCruiseInfo = (cruiseId: number | null) => {
  if (!cruiseId) return undefined;
  
  const cruise = props.selectedItems.cruiseData || 
    (props.selectedItems.cruiseForEdit?.id === cruiseId ? props.selectedItems.cruiseForEdit : null);
    
  // If we don't have the cruise in our props, try to find it in the parent component data
  if (!cruise) {
    // The form will fall back to default behavior without cruise info
    return undefined;
  }
  
  return {
    ducksCreated: cruise.ducksCreated || cruise.ducks?.length || 0,
    maxDucksAllowed: cruise.maxDucksAllowed || 0
  };
};
</script>

<style scoped>
/* Transition animations are handled by Headless UI */
/* Custom package selection styles */
.package-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Focus states for accessibility */
button:focus,
input:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  
  /* Full screen dialog for mobile */
  .mobile-dialog-fullscreen {
    margin: 0;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    width: 100%;
    border-radius: 0;
    overflow: hidden;
  }
}
</style>